/**
 * WelcomeTour.tsx
 *
 * Short, dismissible first-run guidance shown on the home page after onboarding.
 * Introduces the 3-step TeachBud workflow and offers a "Try a Demo" option.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, FileText, Monitor, X, ArrowRight, Sparkles } from 'lucide-react';

const STEPS = [
  {
    step: '1',
    icon: BookOpen,
    color: 'bg-blue-100 text-blue-600',
    title: 'Create a Lesson',
    description: 'Describe the skill you want to teach. TeachBud generates a complete, standards-aligned lesson plan in under a minute.',
  },
  {
    step: '2',
    icon: FileText,
    color: 'bg-violet-100 text-violet-600',
    title: 'Generate Matching Resources',
    description: 'One click creates an activity sheet, assessment, and presentation — all synchronized with your lesson.',
  },
  {
    step: '3',
    icon: Monitor,
    color: 'bg-emerald-100 text-emerald-600',
    title: 'Present, Print, or Assess',
    description: 'Download a PowerPoint, print the activity sheet, or send the assessment — everything is ready to use.',
  },
];

interface WelcomeTourProps {
  onDismiss: () => void;
  onTryDemo: () => void;
}

export function WelcomeTour({ onDismiss, onTryDemo }: WelcomeTourProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="relative bg-gradient-to-br from-primary/5 via-white to-violet-50 rounded-2xl border border-primary/15 p-6 mb-6 overflow-hidden"
    >
      {/* Dismiss */}
      <button
        onClick={onDismiss}
        aria-label="Dismiss welcome guide"
        className="absolute top-4 right-4 h-7 w-7 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100
          flex items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <X size={14} />
      </button>

      {/* Header */}
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Sparkles size={15} className="text-primary" />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900 leading-tight">Welcome to TeachBud 🎉</p>
          <p className="text-xs text-slate-500">Here's how to get started in 3 steps</p>
        </div>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.step} className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-xl ${s.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800 leading-tight mb-0.5">{s.step}. {s.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{s.description}</p>
              </div>
              {i < STEPS.length - 1 && (
                <ArrowRight size={12} className="text-slate-300 mt-1.5 flex-shrink-0 hidden sm:block" />
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          onClick={onTryDemo}
          className="flex items-center gap-1.5 h-9 px-4 rounded-xl bg-primary text-white text-sm font-semibold
            hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Sparkles size={13} />
          Try a Demo Lesson
        </button>
        <button
          onClick={onDismiss}
          className="h-9 px-4 rounded-xl text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100
            transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          Got it, I'll explore on my own
        </button>
      </div>
    </motion.div>
  );
}

/** Returns true if the welcome tour should be shown for this teacher. */
export function shouldShowWelcomeTour(resourceCount: number, tourDismissed: boolean): boolean {
  return resourceCount === 0 && !tourDismissed;
}
